import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { FormsModule } from "@angular/forms";
import { SelectItem, SortMeta } from "primeng/api";
import { ButtonDirective } from "primeng/button";
import { Chip } from "primeng/chip";
import { Dialog } from "primeng/dialog";
import { SelectButton } from "primeng/selectbutton";
import { Subscription } from "rxjs";
import { DatePipe } from "@angular/common";
import { NgxPermissionsModule } from "ngx-permissions";

import { GridParams } from "../../shared/grid/grid-params";
import { GridSortingService } from "../../shared/grid/grid-sorting.service";
import { ORDER } from "../../shared/grid/order.enum";
import { PaginationList } from "../../shared/grid/pagination-list.model";
import { DateRange } from "../../shared/date/date-range.model";
import { noop } from "../../shared/noop.function";
import { BackendErrorPipe } from "../../shared/backend/backend-error.pipe";
import { SafeHtmlPipe } from "../../shared/html/safe-html.pipe";
import { NotifyService } from "../../notify/notify.service";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { UserService } from "../../user/user.service";
import { User } from "../../user/user.model";
import { role } from "../../user/role.const";
import { tableColumn } from "../../ui/table/table-column.const";
import { ITableColumn } from "../../ui/table/table-column.interface";
import { TableComponent } from "../../ui/table/table.component";
import { TableSelectionBarComponent } from "../../ui/table-selection-bar/table-selection-bar.component";
import { QuoteListRowComponent } from "../../ui/table/quote-list-row/quote-list-row.component";
import { Quote } from "../quote.type";
import { PageTitleComponent } from "../../ui/page-title/page-title.component";
import { SearchBoxComponent } from "../../ui/search-box/search-box.component";
import { DatePickerComponent } from "../../ui/date-picker/date-picker.component";
import { AgentRequestMultiselectComponent } from "../../ui/dropdown/agent-request-multiselect/agent-request-multiselect.component";
import { quoteType } from "../quote-form/quote-type.const";
import {
  QuoteListTabsComponent,
  quoteListTab,
} from "../quote-list-tabs/quote-list-tabs.component";
import {
  AgentRequest,
  agentRequestGroup,
  agentRequestStatus,
} from "../models/agent-request.model";
import { AgentRequestRepository } from "../agent-request.repository";
import {
  agentRequestListParams,
  agentRequestSender,
} from "./agent-request-list-params.const";

@Component({
  selector: "app-agent-request-list",
  templateUrl: "./agent-request-list.component.html",
  styleUrls: ["./agent-request-list.component.scss"],
  providers: [
    GridParams,
    { provide: GridParams.DEFAULTS, useValue: {} },
    { provide: GridParams.STORAGE_KEY, useValue: "agent-request-list" },
    GridSortingService,
    {
      provide: GridSortingService.SORTING_FIELDS,
      useValue: ["status", "updatedAt", "sentAt"],
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgxPermissionsModule,
    FormsModule,
    DatePipe,
    ButtonDirective,
    Chip,
    Dialog,
    SelectButton,
    PageTitleComponent,
    SearchBoxComponent,
    DatePickerComponent,
    TableComponent,
    TableSelectionBarComponent,
    QuoteListRowComponent,
    AgentRequestMultiselectComponent,
    QuoteListTabsComponent,
    SafeHtmlPipe,
  ],
})
export class AgentRequestListComponent implements OnInit, OnDestroy {
  public gridParams = inject(GridParams);
  private sortingService = inject(GridSortingService);
  private agentRequestRepository = inject(AgentRequestRepository);
  private notifyService = inject(NotifyService);
  private userService = inject(UserService);

  public readonly role = role;
  public readonly activeTab = quoteListTab.requests;
  public readonly typeOptions: SelectItem[] = [
    { label: "All", value: null },
    { label: "Rates", value: agentRequestGroup.rates },
    { label: "Charges", value: agentRequestGroup.charges },
  ];
  public readonly senderOptions: SelectItem[] = [
    { label: "All", value: agentRequestSender.all },
    { label: "My request", value: agentRequestSender.mine },
  ];
  public readonly quoteTypeOptions: SelectItem[] = [
    { label: "FCL", value: quoteType.fcl },
    { label: "LCL", value: quoteType.lcl },
    { label: "AIR", value: quoteType.air },
    { label: "LTL", value: quoteType.ltl },
  ];
  public readonly statusOptions: SelectItem[] = [
    { label: "Requested", value: agentRequestStatus.requested },
    { label: "Closed", value: agentRequestStatus.closed },
  ];
  public readonly columns: ITableColumn[] = [
    {
      // No open tracking: an absent field shows the same envelope on every row.
      header: "<i class='pi pi-envelope'></i>",
      field: "openedAt",
      type: tableColumn.opened,
      hideOnMobile: true,
    },
    {
      header: "Status",
      field: "status",
      type: tableColumn.status,
      sortable: true,
    },
    {
      header: "Updated at",
      field: "updatedAt",
      type: tableColumn.timeAgo,
      sortable: true,
      hideOnMobile: true,
    },
    { header: "Agent name", field: "agentName", type: tableColumn.default },
    {
      header: "Client name",
      field: "clientName",
      type: tableColumn.default,
      hideOnMobile: true,
    },
    { header: "Details", field: "quote", type: tableColumn.relatedQuote },
    {
      header: "Request type",
      field: "typeLabel",
      type: tableColumn.default,
    },
    {
      header: "Quote ID",
      field: "quoteId",
      type: tableColumn.textLink,
      href: (row) => (row as unknown as AgentRequest).getQuoteUrl(),
    },
    {
      header: "Quote validity",
      field: "quoteValidTo",
      type: tableColumn.date,
      hideOnMobile: true,
    },
    {
      header: "Sent at",
      field: "sentAt",
      type: tableColumn.datetime,
      sortable: true,
      hideOnMobile: true,
    },
    {
      header: "Sent by",
      field: "sentByName",
      type: tableColumn.default,
      hideOnMobile: true,
    },
  ];

  public user: User = null;
  public list = new PaginationList<AgentRequest>();
  public selectedRequests: AgentRequest[] = [];
  public loading = false;
  public searchQuery = "";
  public selectedGroup: string | null = null;
  public sender: string = agentRequestSender.all;
  public selectedQuoteTypes: string[] = [];
  public selectedStatuses: string[] = [];
  public createdAtRange: DateRange | null = null;
  public selectedRequest: AgentRequest | null = null;
  // The details payload leaves the quote out; the clicked row carries it.
  public selectedQuote: Quote | null = null;
  public detailsVisible = false;

  private subscription = new Subscription();
  private fetchSubscription?: Subscription;

  ngOnInit(): void {
    if (this.gridParams.getParam(agentRequestListParams.quote)) {
      this.resetStoredFilters();
    }
    this.loadParams();
    this.subscription.add(
      this.gridParams.onChange.subscribe(() => this.fetchRequests()),
    );
    this.userService.getUser().then((user) => {
      this.user = user;
      this.fetchRequests();
    }, noop);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onLoadPage(page: number, rows: number): void {
    this.gridParams.setParams({
      [agentRequestListParams.page]: page,
      [agentRequestListParams.limit]: rows,
    });
  }

  public onSortGrid({ field, order }: SortMeta): void {
    this.sortingService.sortGrid(field, ORDER[order]);
  }

  public onSearchQueryChange(): void {
    this.gridParams.setParams({
      [agentRequestListParams.search]: this.searchQuery,
      [agentRequestListParams.page]: 1,
    });
  }

  public onGroupChange(group: string | null): void {
    this.selectedGroup = group;
    this.setFilter(agentRequestListParams.type, group);
  }

  public onSenderChange(sender: string): void {
    this.sender = sender;
    this.setFilter(
      agentRequestListParams.sender,
      sender === agentRequestSender.mine ? sender : null,
    );
  }

  public onQuoteTypesChange(quoteTypes: string[]): void {
    this.selectedQuoteTypes = quoteTypes;
    this.setFilter(agentRequestListParams.quoteType, this.join(quoteTypes));
  }

  public onStatusesChange(statuses: string[]): void {
    this.selectedStatuses = statuses;
    this.setFilter(agentRequestListParams.status, this.join(statuses));
  }

  public onCreatedAtRangeChange(range: DateRange | null): void {
    this.createdAtRange = range;
    this.gridParams.setParams({
      [agentRequestListParams.createdAtFrom]: this.toLocalDateString(
        range?.getStart(),
      ),
      [agentRequestListParams.createdAtTo]: this.toLocalDateString(
        range?.getEnd(),
      ),
      [agentRequestListParams.page]: 1,
    });
  }

  public getQuoteChipLabel(): string | null {
    const quoteId = this.gridParams.getParam(agentRequestListParams.quote);
    if (!quoteId) {
      return null;
    }
    const locationName = this.gridParams.getParam(
      agentRequestListParams.locationName,
    );

    return [`Quote ${quoteId}`, locationName].filter(Boolean).join(" · ");
  }

  public removeQuoteChip(): void {
    this.gridParams.setParams({
      [agentRequestListParams.quote]: null,
      [agentRequestListParams.location]: null,
      [agentRequestListParams.locationName]: null,
      [agentRequestListParams.page]: 1,
    });
  }

  public isClearFiltersVisible(): boolean {
    return !!(
      this.selectedGroup ||
      this.sender === agentRequestSender.mine ||
      this.selectedQuoteTypes.length ||
      this.selectedStatuses.length ||
      this.createdAtRange ||
      this.getQuoteChipLabel()
    );
  }

  public clearFilters(): void {
    this.resetStoredFilters();
    this.gridParams.setParam(agentRequestListParams.type, null, false);
    this.loadParams();
    this.removeQuoteChip();
  }

  public onClickRow(request: AgentRequest): void {
    this.subscription.add(
      this.agentRequestRepository.getAgentRequest(request.getId()).subscribe({
        next: (details) => {
          this.selectedRequest = details;
          this.selectedQuote = request.quote;
          this.detailsVisible = true;
        },
        error: (err) =>
          this.notifyError("Agent request has not been fetched", err),
      }),
    );
  }

  // Adds the current page only: the other pages are not loaded.
  public selectAll(): void {
    this.selectedRequests = TableSelectionBarComponent.selectPage(
      this.selectedRequests,
      this.list.items,
    );
  }

  public clearSelection(): void {
    this.selectedRequests = [];
  }

  public hasRequestedSelected(): boolean {
    return this.selectedRequests.some(
      (request) => request.status === agentRequestStatus.requested,
    );
  }

  public closeSelected(): void {
    this.subscription.add(
      this.notifyService
        .confirm("Are you sure you want to close selected requests?", "Close", {
          confirmLabel: "Close",
          denyLabel: "Cancel",
        })
        .subscribe(() => this.closeRequests()),
    );
  }

  public fetchRequests(): void {
    if (!this.user) {
      return;
    }
    this.fetchSubscription?.unsubscribe();
    this.loading = true;
    this.fetchSubscription = this.agentRequestRepository
      .getAgentRequestList(this.buildHttpParams())
      .subscribe({
        next: (list) => (this.list = list),
        error: (err) => {
          this.loading = false;
          this.notifyError("Agent request list has not been fetched", err);
        },
        complete: () => (this.loading = false),
      });
    this.subscription.add(this.fetchSubscription);
  }

  private closeRequests(): void {
    this.subscription.add(
      this.agentRequestRepository
        .close(this.selectedRequests.map((request) => request.getId()))
        .subscribe({
          next: () => {
            this.notifyService.toast(TOAST.INFO, "Requests closed");
            this.clearSelection();
            this.fetchRequests();
          },
          error: (err) =>
            this.notifyError("Agent requests have not been closed", err),
        }),
    );
  }

  private setFilter(name: string, value: string | null): void {
    this.gridParams.setParams({
      [name]: value,
      [agentRequestListParams.page]: 1,
    });
  }

  // The Freight and Charges links carry their own type, so it is only cleared on demand.
  private resetStoredFilters(): void {
    this.gridParams.setParams(
      {
        [agentRequestListParams.sender]: null,
        [agentRequestListParams.quoteType]: null,
        [agentRequestListParams.status]: null,
        [agentRequestListParams.createdAtFrom]: null,
        [agentRequestListParams.createdAtTo]: null,
      },
      false,
    );
  }

  private loadParams(): void {
    this.searchQuery = this.gridParams.getParam(agentRequestListParams.search);
    this.selectedGroup =
      this.gridParams.getParam(agentRequestListParams.type) || null;
    this.sender =
      this.gridParams.getParam(agentRequestListParams.sender) ??
      agentRequestSender.all;
    this.selectedQuoteTypes = this.split(
      this.gridParams.getParam(agentRequestListParams.quoteType),
    );
    this.selectedStatuses = this.split(
      this.gridParams.getParam(agentRequestListParams.status),
    );
    this.createdAtRange = this.loadCreatedAtRange();
  }

  private loadCreatedAtRange(): DateRange | null {
    const from = this.parseLocalDate(
      this.gridParams.getParam(agentRequestListParams.createdAtFrom),
    );
    const to = this.parseLocalDate(
      this.gridParams.getParam(agentRequestListParams.createdAtTo),
    );

    return from || to ? new DateRange(from, to) : null;
  }

  private buildHttpParams(): HttpParams {
    let params = this.gridParams
      .getHttpParams()
      .delete(agentRequestListParams.sender)
      .delete(agentRequestListParams.locationName);
    if (this.sender === agentRequestSender.mine) {
      params = params.set(agentRequestListParams.sentBy, this.user.getId());
    }

    return params;
  }

  private notifyError(title: string, err: unknown): void {
    this.notifyService.toast(
      TOAST.ERROR,
      title,
      new BackendErrorPipe().transform(err),
    );
  }

  private join(values: string[]): string | null {
    return values.length ? values.join(",") : null;
  }

  private split(value: string | null): string[] {
    return value ? value.split(",") : [];
  }

  private toLocalDateString(date: Date | null | undefined): string | null {
    if (!date) {
      return null;
    }

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  private parseLocalDate(value: string | null): Date | null {
    if (!value) {
      return null;
    }
    const [year, month, day] = value.split("-").map(Number);

    return new Date(year, month - 1, day);
  }
}
